import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, from, forkJoin, throwError } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';
import { SubscriptionService } from './subscription.service';
import { ScheduleService } from './schedule.service';
import { LoginError, LoginResponse, OTPRequestResponse, OTPVerifyRequest, OTPRequestRequest } from '../interfaces/auth.interface';
import { ApiService } from './api.service';

interface LoginRequest {
  company_uuid: string;
  email_id?: string;
  mobile_country_code?: string;
  mobile_number?: number;
  password: string;
}

interface UserInfo {
  email: string;
  client_name: string;
  client_image?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isLoggedInSubject = new BehaviorSubject<boolean>(false);
  isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  userInfo$ = this.userInfoSubject.asObservable();

  constructor(
    private http: HttpClient,
    private subscriptionService: SubscriptionService,
    private scheduleService: ScheduleService,
    private apiService: ApiService
  ) {
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Check if token is expired
      if (this.isTokenExpired(token)) {
        this.logout();
        return;
      }

      const userInfo = this.extractUserInfoFromToken(token);
      if (userInfo) {
        this.userInfoSubject.next(userInfo);
        this.isLoggedInSubject.next(true);
        // Fetch both subscriptions and schedule if token exists
        forkJoin([
          this.subscriptionService.fetchSubscriptions(),
          this.scheduleService.fetchSchedule()
        ]).subscribe();
      }
    }
  }

  private get apiUrl(): string {
    return `${this.apiService.getBaseApiUrl()}/login/verify_token`;
  }

  private get otpRequestUrl(): string {
    return `${this.apiService.getBaseApiUrl()}/login/request_otp`;
  }

  private get otpVerifyUrl(): string {
    return `${this.apiService.getBaseApiUrl()}/login/verify_token`;
  }

  private extractUserInfoFromToken(token: string): UserInfo | null {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        email: payload.email,
        client_name: payload.client_name,
        client_image: payload.client_image
      };
    } catch (e) {
      console.error('Error decoding JWT token:', e);
      return null;
    }
  }

  verifyCredentials(identifier: string, password: string, companyUuid: string, countryCode?: string): Observable<LoginResponse> {
    if (!companyUuid) {
      console.error('company_uuid is required for login');
      return throwError(() => new Error('company_uuid is required'));
    }

    const loginRequest: LoginRequest = {
      company_uuid: companyUuid,
      password: password
    };

    // Check if identifier is email or phone
    if (identifier.includes('@')) {
      loginRequest.email_id = identifier;
    } else {
      loginRequest.mobile_country_code = countryCode || '+91';
      loginRequest.mobile_number = parseInt(identifier, 10);
    }

    return this.http.post<LoginResponse>(this.apiUrl, loginRequest).pipe(
      tap((response: LoginResponse) => {
        if (response.success) {
          this.setTokens(response.token, response.refresh_token);
        }
      }),
      switchMap((response: LoginResponse) => {
        if (response.success) {
          return forkJoin([
            this.subscriptionService.fetchSubscriptions(),
            this.scheduleService.fetchSchedule()
          ]).pipe(
            map(() => response)
          );
        }
        return from([response]);
      })
    );
  }

  private setTokens(token: string, refreshToken: string): void {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('refresh_token', refreshToken);
    const userInfo = this.extractUserInfoFromToken(token);
    if (userInfo) {
      this.userInfoSubject.next(userInfo);
      this.isLoggedInSubject.next(true);
    }
  }

  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_subscriptions');
    localStorage.removeItem('subscription_fetch_timestamp');
    localStorage.removeItem('user_schedule');
    localStorage.removeItem('schedule_fetch_timestamp');
    this.userInfoSubject.next(null);
    this.isLoggedInSubject.next(false);
  }

  isLoggedIn(): boolean {
    return this.isLoggedInSubject.value;
  }

  getUserInfo(): UserInfo | null {
    return this.userInfoSubject.value;
  }

  private isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiry = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= expiry;
    } catch (e) {
      console.error('Error checking token expiry:', e);
      return true; // Treat as expired if we can't verify
    }
  }

  checkTokenValidity(): boolean {
    const token = localStorage.getItem('auth_token');
    if (!token) return false;

    if (this.isTokenExpired(token)) {
      this.logout(); // Clear expired token and user data
      return false;
    }

    return true;
  }

  requestOTP(request: OTPRequestRequest): Observable<OTPRequestResponse> {
    if (!request.company_uuid) {
      return throwError(() => new Error('company_uuid is required'));
    }

    return this.http.post<OTPRequestResponse>(this.otpRequestUrl, request);
  }

  verifyOTP(request: OTPVerifyRequest): Observable<LoginResponse> {
    if (!request.company_uuid || !request.otp_code || !request.otp_uuid) {
      return throwError(() => new Error('Missing required parameters'));
    }

    return this.http.post<LoginResponse>(this.otpVerifyUrl, request).pipe(
      tap((response: LoginResponse) => {
        if (response.success) {
          this.setTokens(response.token, response.refresh_token);
        }
      }),
      switchMap((response: LoginResponse) => {
        if (response.success) {
          return forkJoin([
            this.subscriptionService.fetchSubscriptions(),
            this.scheduleService.fetchSchedule()
          ]).pipe(
            map(() => response)
          );
        }
        return from([response]);
      })
    );
  }
} 